import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationStart, Router } from '@angular/router';
import { AccountConfigDTO, ConfigDataDTO, UsersDTO } from '@DTOs';
import { RolesEnum } from '@Enums';
import { UWResponse } from '@Models';
import moment from 'moment-timezone';
import { BehaviorSubject, Observable, Subject, Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize, map, skip, take } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { ErrorNotificationComponent } from './components/error/error-notification.component';
import { IConfigService } from './config/iconfigservice';
import { ActionContext } from './action-context';
import { MonitoringService } from './monitor.service';
import { NotificationService, NotificationSeverity } from './services/notification.service';
import { SignalRService } from './services/signalr.service';

@Injectable()
export class AppService implements OnDestroy {

    subs = new SubSink();
    username: string;
    userid: string;
    authToken: any = '';
    acl: any = [];
    userSettings;
    theme = 'theme-1';
    emptyGUID = '00000000-0000-0000-0000-000000000000';
    combosList: any = [
        { type: 'AccountTypes', version: 1, property: 'accountTypes' },
        { type: 'Status', version: 1, property: 'statuses' },
        { type: 'ContactTypes', version: 1, property: 'contactTypes' },
        { type: 'ActionTypes', version: 1, property: 'actionTypes' },
        { type: 'Actions', version: 1, property: 'actions' },
    ];

    combos = [];

    dataTypes = {
        string: ['varchar', 'uniqueidentifier', 'nvarchar', 'str'],
        number: ['int', 'long', 'short', 'float', 'double', 'bigdecimal', 'money', 'decimal', 'Double'],
        date: ['datetime', 'date', 'Date', 'datetime.datetime']
    };

    private configData: ConfigDataDTO;
    private dynamicConfigData: AccountConfigDTO;

    private isRegenerating: boolean;
    private orderReports: any;
    private previousUrl: string = undefined;
    private currentUrl: string = undefined;

    // public status: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public status: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    callCount = 0;
    public secondaryLoadingMask: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public isFetchingInitialConfigData = false;
    public forceHideLoadingIndicator = false;

    public sideMenu = new Subject<any>();
    sideMenuObservable = this.sideMenu.asObservable();

    private routerSubscription: Subscription;

    public runtimeCondensedViewEnabled = false;
    public applicationTimingEnabled = false;
    userHasMyCasesQueue: boolean;
    userIsTeamAdmin: boolean;

    getIsRegenerating() {
        return this.isRegenerating;
    }

    setIsRegenerating(value) {
        this.isRegenerating = value;
    }

    getOrderReports() {
        return this.orderReports;
    }

    setOrderReports(data) {
        this.orderReports = data;
    }

    constructor(
        public dialog: MatDialog,
        private router: Router,
        private httpClient: HttpClient,
        private configService: IConfigService,
        private notificationService: NotificationService,
        private signalRService: SignalRService,
        private monitoringService: MonitoringService
    ) {
        this.routerSubscription = router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                if (!this.currentUrl) {
                    this.currentUrl = event.url;
                } else {
                    this.previousUrl = this.currentUrl;
                    this.currentUrl = event.url;
                }
            }
        });

        this.subs.add(this.routerSubscription);
    }

    //#region Helpers

    /**
     * Show or hide the "global spinner" overlay.
     * This method keeps track of the number of callers (ie. callCount) such that if multiple callers are showing a spinner, we need an equal number
     * of callers disabling the spinner for the spinner to be disabled.
     * @param value Show (true) or hide (false) the spinner.
     * @param hideWaiting Set true to avoid adding showing the spinner. Useful for api calls you can run in the background.
     */
    display(value: boolean, hideWaiting = false, url = '') {

        if (hideWaiting) return;

        if (this.callCount < 0) this.callCount = 0;

        if (value && this.callCount === 0) {
            this.monitoringService.logEvent("Spinner opened", {"eventType": "Client Runtime", "MethodName": url});
        }

        if (value) this.callCount += 1;
        else this.callCount -= 1;
      
        if (!value && this.callCount <= 0 ) {
            this.monitoringService.logEvent("Spinner closed", {"eventType": "Client Runtime"});
        }

        this.status.next(this.callCount);
    }

    trackInterviewMetric(eventName: string, metricProperties: { [key: string]: string }) {
        if (!this.applicationTimingEnabled) return;

        const event = this.monitoringService.beginInterviewMetric(eventName, metricProperties);

        // End the metric when the global spinner clears (status drops to 0).
        // skip(1) ignores the current value so we only react to future emissions.
        this.status.pipe(
            skip(1),
            filter(count => count === 0),
            take(1)
        ).subscribe(() => {
            this.monitoringService.endInterviewMetric(event);
            this.monitoringService.flushInterviewMetrics();
        });
    }

    //#endregion

    //#region Lifecycle

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    //#endregion

    public getPreviousUrl() {
        return this.previousUrl;
    }

    getTimeZone() {
        const dateValue = moment(new Date());
        return dateValue.tz(this.configService.getConfiguration().timeZone).format('z');
    }

    getSideMenuInfo(value) {
        this.sideMenu.next(value);
    }


    showSecondaryLoadingMask(value: boolean) {
        this.secondaryLoadingMask.next(value);
    }

    getAuthToken() {
        const legacy = (window.localStorage.getItem('legacy') === 'true');
        const token = window.localStorage.getItem('access_token');

        if (!legacy && token && token !== null) {
            return token;
        }

        return window.localStorage.getItem('id_token');
    }

    setAuthToken(token) {
        window.localStorage.setItem('authToken', token);
    }

    getACL() {
        this.acl = JSON.parse(window.localStorage.getItem('acl'));
        return this.acl;
    }

    setACL(acl) {
        this.acl = Object.assign({}, ...acl);
        window.localStorage.setItem('acl', JSON.stringify(this.acl));
    }

    checkACL(feature, permissionType = 'R', redirect?) {

        const levels = { 'N': 0, 'R': 1, 'V': 1, 'W': 2, 'F': 3 };

        // NOTE: We cannot switch this area over to the routing service yet, as it creates a circular dependency.
        //       Need to clean this up when we do the permissions revamp.

        let havePermission;
        if (!this.acl || this.acl.length === 0) {
            // The ACL is entirely missing, we need to route through login to go get it.
            // This needs to be cleaned up, but leaves current state in tact.
            console.log('Empty ACL, routing through login.');
            this.router.navigate(['login']);
            return false;
        } else if (!this.acl[feature]) {
            // A single feature is missing.
            havePermission = false;
        } else {
            havePermission = (levels[this.acl[feature].charAt(0)] >= levels[permissionType] ? true : false);
        }

        if (havePermission === true) {
            return true;
        } else if (redirect) {
            this.router.navigate([redirect]);
            return false;
        } else {
            return false;
        }
    }

    hasViewPermission(feature) {
        return (this.acl[feature] === 'VIEW' ? true : false);
    }

    showResponseErrorMsg(response) {
        if (response.status === 0 || !response.error) {
            this.monitoringService.logError(new Error(`An error occurred: ${JSON.stringify(response)}`));
            console.log('response:', response);
            return;
        }

        if (response.status === 400 && response.error === "Bad request") {
            this.monitoringService.logError(new Error('An error occurred indicating a bad request. Please verify your request is valid.'));
            console.log('response:', response);
            return;
        }

        if (response.status === 404) {
            response.error = {
                sourceError: 'An unknown error has occurred.',
                userErrors: [
                    `${response.message}`
                ]
            };
        }

        if (response.error?.isAuthenticationFailure) {
            return;
        }

        this.dialog.open(ErrorNotificationComponent, {
            data: response.error,
            width: "500px"
        });
    }

    //#region  API Callers

    getData<T = any>(urlPath: string, clientId?: string, hideWaiting = false) {
        this.display(true, hideWaiting, urlPath);

        return this.httpClient.get(this.getAPIBaseURL() + urlPath, {
            headers: this.getHeaders(clientId)
        }).pipe(
            map((response: T) => {
                if (response) return response;
            }),
            catchError(error => {
                this.showResponseErrorMsg(error);
                return throwError(() => new Error(error.message));
            }),
            finalize(() => {
                this.display(false, hideWaiting);
            })
        );
    }

    getDataWithID<T = any>(urlPath: string, ID, clientId?: string, hideWaiting = false) {
        const params: HttpParams = new HttpParams();
        params.set('ID', ID);

        return this.httpClient.get(this.getAPIBaseURL() + urlPath, {
            headers: this.getHeaders(clientId),
            params: params
        }).pipe(
            map((res: T) => {
                return res;
            }),
            catchError(error => {
                this.showResponseErrorMsg(error);
                return throwError(() => new Error(error.message));
            })
        );
    }

    postData<T = any>(urlPath, data: any, isEdit = false, addId = true, clientId?: string, hideWaiting = false) {
        this.display(true, hideWaiting, urlPath);

        // DEV NOTE: Callers of this method need <any> or we'll get typescript transpile errors. Need to investigate why so we can fix typings.
        let response: Observable<T> = null;

        if (!isEdit) {
            delete data['id'];

            response = this.httpClient.post(this.getAPIBaseURL() + urlPath, data, {
                headers: this.getHeaders(clientId)
            }).pipe(
                map((res: T) => {
                    if (res) {
                        return res;
                    }
                }),
                catchError((error: any) => {
                    this.showResponseErrorMsg(error);
                    return throwError(() => new Error(error.message));
                }),
                finalize(() => {
                    this.display(false, hideWaiting);
                })
            );

        } else {
            response = this.httpClient.put(this.getAPIBaseURL() + urlPath + (addId ? '/' + data.id : ''), data, {
                headers: this.getHeaders(clientId)
            }).pipe(
                map((res: T) => {
                    if (res) {
                        return res;
                    }
                }),
                catchError((error: any) => {
                    this.showResponseErrorMsg(error);
                    return throwError(() => new Error(error.message));
                }),
                finalize(() => {
                    this.display(false, hideWaiting);
                })
            );
        }

        return response;
    }

    addData<T = any>(urlParams: string, data: any, clientId?: string, hideWaiting = false) {
        this.display(true, hideWaiting, urlParams);

        const response = this.httpClient.post(this.getAPIBaseURL() + urlParams, data, {
            headers: this.getHeaders(clientId)
        }).pipe(
            map((response: T) => {
                if (response) {
                    return response;
                }
            }),
            catchError((error: any) => {
                this.showResponseErrorMsg(error);
                return throwError(() => new Error(error.message));
            }),
            finalize(() => {
                this.display(false, hideWaiting);
            })
        );

        return response;
    }

    sendData(urlPath: string, data: any, clientId?: string) {
        this.subs.add(this.httpClient.post(this.getAPIBaseURL() + urlPath, data, {
            headers: this.getHeaders(clientId)
        }).subscribe());
    }

    updateData<T = any>(urlPath: string, data: any, clientId?: string, hideWaiting = false) {
        this.display(true, hideWaiting, urlPath);

        const response = this.httpClient.put(this.getAPIBaseURL() + urlPath, data, { headers: this.getHeaders(clientId) }).pipe(
            map((res: T) => {
                if (res) {
                    return res;
                }
            }),
            catchError((response: any) => {
                if (response.error.suppressNotifications) {
                    return throwError(() => new Error(JSON.stringify(response.error)));
                }

                this.showResponseErrorMsg(response);
                return throwError(() => new Error(response.message));
            }),
            finalize(() => {
                this.display(false, hideWaiting);
            })
        );

        return response;
    }

    deleteData<T = any>(urlPath: string, clientId?: string, hideWaiting = false) {
        this.display(true, hideWaiting, urlPath);

        const response = this.httpClient.delete(this.getAPIBaseURL() + urlPath, {
            headers: this.getHeaders(clientId)
        }).pipe(
            map((res: T) => {
                if (res) {
                    return res;
                }
            }),
            catchError((error: any) => {
                this.display(false);
                this.showResponseErrorMsg(error);
                return throwError(() => new Error(error.message));
            }),
            finalize(() => {
                this.display(false, hideWaiting);
            })
        );

        return response;
    }

    deleteDataWithID<T = any>(urlPath: string, ID, clientId?: string) {
        const params: HttpParams = new HttpParams();
        params.set('ID', ID);

        return this.httpClient.delete(this.getAPIBaseURL() + urlPath, {
            headers: this.getHeaders(clientId),
            params: params
        }).pipe(map((res: T) => res));
    }

    callAPI<T = any>(params, clientId?: string, hideWaiting = false) {
        this.display(true, hideWaiting, 'Conversions/api/');

        const response = this.httpClient.post(this.getAPIBaseURL() + 'Conversions/api/', params, {
            headers: this.getHeaders(clientId)
        }).pipe(
            map((res: T) => {
                if (res) {
                    return res;
                }
            }),
            catchError((error: any) => {
                return throwError(() => new Error(error.message));
            }),
            finalize(() => {
                this.display(false, hideWaiting);
            })
        );

        return response;
    }

    //#endregion

    getSocketUrl() {
        const currentApiUrl = this.configService.getConfiguration().apiUrl;
        let socketUrl = currentApiUrl.split('api/v1/')[0].replace('http://', 'ws://');
        socketUrl = socketUrl.split('api/v1/')[0].replace('https://', 'wss://');
        socketUrl = socketUrl + 'notify';
        return socketUrl;

    }

    getClientId(overrideClientId: string) {
        if (overrideClientId) { return overrideClientId; }

        let route = this.router.routerState.root;
        while (route.firstChild) {
            // iterate down to the bottom, we can only start from root.
            route = route.firstChild;
        }

        const routeData = route.snapshot.data;
        return routeData && routeData.client ? routeData.client.id : undefined;
    }

    getAccountCode(overrideAccountCode?: string) {
        if (overrideAccountCode) { return overrideAccountCode; }

        let route = this.router.routerState?.root;
        if (!route) { return undefined; }
        while (route.firstChild) {
            route = route.firstChild;
        }

        const routeData = route?.snapshot?.data;
        if (routeData?.account?.code) {
            return routeData.account.code;
        }

        return route?.snapshot?.params?.accountCode;
    }

    getHeaders(overrideClientId?: string) {
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', 'Bearer ' + this.getAuthToken());

        const clientId = this.getClientId(overrideClientId);
        if (clientId != null) {
            headers = headers.append('X-Tenant-Guid', clientId);
        }

        const accountCode = this.getAccountCode();
        if (accountCode != null) {
            headers = headers.append('X-Account-Code', accountCode);
        }

        const sessionId = this.signalRService.getSessionId();
        if (sessionId != null) {
            headers = headers.append('X-Session-Id', sessionId);
        }

        const requestId = this.monitoringService.generateRequestId();
        headers = headers.append('X-Request-Id', requestId);

        const actionId = ActionContext.get();
        if (actionId) {
            headers = headers.append('X-Action-Id', actionId);
        }

        return headers;
    }

    getUploadHttpHeaders(overrideClientId?: string) {
        let headers = new HttpHeaders();
        headers = headers.append('Authorization', 'Bearer ' + this.getAuthToken());

        const clientId = this.getClientId(overrideClientId);
        if (clientId != null) {
            headers = headers.append('X-Tenant-Guid', clientId);
        }

        const accountCode = this.getAccountCode();
        if (accountCode != null) {
            headers = headers.append('X-Account-Code', accountCode);
        }

        const requestId = this.monitoringService.generateRequestId();
        headers = headers.append('X-Request-Id', requestId);

        const actionId = ActionContext.get();
        if (actionId) {
            headers = headers.append('X-Action-Id', actionId);
        }

        return headers;
    }

    formatForDateTimeFormat(config) {
        let isUTCConvNeed = false;
        let sortOrderCol;
        if (config.xAxis && config.xAxis.type === 'datetime') {
            isUTCConvNeed = true;
        }
        if (isUTCConvNeed) {
            for (let a = 0; a < config.series.length; a++) {
                const dataofChart = config.series[a].data;
                for (let i = 0; i < dataofChart.length; i++) {
                    for (let j = 0; j < dataofChart[i].length; j++) {
                        if (dataofChart[i][j].indexOf && dataofChart[i][j].indexOf('UTC')) {
                            const tempdate = new Date(dataofChart[i][j].substr(0, dataofChart[i][j].indexOf('UTC')));
                            sortOrderCol = j;
                            dataofChart[i][j] = Date.UTC(tempdate.getFullYear(), tempdate.getMonth(), tempdate.getDate());
                        }
                    }
                }
                dataofChart.sort(function (data1, data2) {
                    return data2[sortOrderCol] - data1[sortOrderCol];
                });
            }
        }
    }

    getUsername(): string {
        return this.username;
    }

    getUserId(): string {
        return window.localStorage.getItem('userid');
    }

    getUserRoleId(): string {
        return window.localStorage.getItem('userroleid');
    }

    getUserRoleName(): string {
        return this.getUserDataItems('userRoleName');
    }

    getIsAgentBehavior() {
        const userRoleName = this.getUserRoleName();
        return userRoleName === RolesEnum.Agent
            || userRoleName === RolesEnum.ClientCSR
            || userRoleName === RolesEnum.ClientSupervisor
            || userRoleName === RolesEnum.Applicant;
    }

    setUserdetails(data: UsersDTO) {
        // var username = data.username;
        const username = data.firstName + ' ' + data.lastName;
        const userid = data.id;
        this.username = username;
        this.userHasMyCasesQueue = data.hasMyCasesQueue;
        this.userIsTeamAdmin = data.isTeamAdmin;
        const userData = JSON.stringify(data);
        window.localStorage.setItem('data', userData);
        // window.localStorage.setItem("Settings", this.getUserDataItems("Settings"));
        window.localStorage.setItem('userid', userid);
        window.localStorage.setItem('userroleid', data.userRoleId);
    }

    getUserDataItems<T = string>(key: string): T {
        const data = JSON.parse(localStorage.getItem('data'));
        const keyItem: T = data[key] ? data[key] : '';
        return keyItem;
    }

    getAPIBaseURL() {
        let url = this.configService.getConfiguration().apiUrl;
        return url;
    }

    isValidJSON(string) {
        let isValidJson = string ? true : false;
        try { JSON.parse(string); } catch (e) { isValidJson = false; }
        return isValidJson;
    }

    showMsg(msgType = 'default', msg = '', logError = true) {
        /* STOP USING THIS.  Go directly at the notificationService, and convert areas targeting this
           function as you encounter them, so that we can phase it out of app.service. */

        if (msgType === 'error') {
            this.display(false); // This will get phased out as we stop using this, seems like a separate concern.
        }

        this.notificationService.showNotification({
            severity: msgType as NotificationSeverity,
            message: msg,
            log: logError && (msgType === 'error')
        });
    }

    getAllConfigData(): Promise<ConfigDataDTO> {
        return new Promise((resolve, _reject) => {
            if (this.configData !== undefined) {
                resolve(this.configData);
            } else {
                this.subs.add(this.getData<UWResponse<ConfigDataDTO>>(`ConfigData`).subscribe({
                    next: (response) => {
                        if (response.status === 'success') {
                            this.configData = response.data;
                        }
                        resolve(this.configData);
                    },
                    error: () => {
                        resolve(this.configData);
                    }
                }));
            }
        });
    }

    getConfigData(configName: string) {
        // @revisit:Should this call getAllConfigData to resolve configData instead of assuming it is loaded?
        if (this.configData && this.configData[configName] && this.configData[configName].length) {
            return this.configData[configName];
        } else {
            return [];
        }
    }

    getAllDynamicConfigData(accountId) {
        return new Promise((resolve, _reject) => {
            this.subs.add(this.getData<UWResponse<AccountConfigDTO>>(`ConfigData/ByAccount/${accountId}`).subscribe({
                next: (response) => {
                    if (response.status === 'success') {
                        this.dynamicConfigData = response.data;
                    }
                    resolve(this.dynamicConfigData);
                },
                error: (_error) => {
                    resolve(this.dynamicConfigData);
                }
            }));
        });
    }

    getDynamicConfigData(configName) {
        // @revisit:Should this call getAllConfigData to resolve configData instead of assuming it is loaded?
        if (this.dynamicConfigData && this.dynamicConfigData[configName] && this.dynamicConfigData[configName].length) {
            return this.dynamicConfigData[configName];
        } else {
            return [];
        }
    }

    getConfigFromStaticAndDynamicConfigData(configName) {
        if (this.dynamicConfigData && this.dynamicConfigData[configName] && this.dynamicConfigData[configName].length) {
            return this.dynamicConfigData[configName];
        } else if (this.configData && this.configData[configName] && this.configData[configName].length) {
            return this.configData[configName];
        } else {
            return [];
        }
    }

    getDefaultLogo() {
        return 'data:image/png;base64,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';
    }

    getCombos() {
        this.getAllConfigData().then((configData) => {
            this.combosList.forEach(combo => {
                this.combos[combo.type] = configData[combo.property];;
            });
        });
    }

    formatDate(dateString, includeTime = false) {
        const nextDate = new Date(dateString);

        let formatedDate = (nextDate.getMonth() + 1) + '/' + nextDate.getDate() + '/' + nextDate.getFullYear();

        if (includeTime) {
            formatedDate += ' ' + nextDate.getHours() + ':' + nextDate.getMinutes() + ':' + nextDate.getSeconds();
        }

        return formatedDate;
    }

    focusElement(element) {
        setTimeout(() => {
            if (element && element.nativeElement) { element.nativeElement.focus(); }
        });
    }

    getDateString(dateObject) {
        return (dateObject.getMonth() + 1) + '/' +
            dateObject.getDate() + '/' +
            dateObject.getFullYear();
    }

    flushRuleExecutionLogs(caseId: string, overrideClientId?: string) {
        const clientId = this.getClientId(overrideClientId);
        const url = `${this.configService.getConfiguration().publicApiUrl}client/${clientId}/case/${caseId}/executionlogs/rules`;

        return this.httpClient.put(url, this.monitoringService.getRuleExecutionLogs(), {
            headers: this.getHeaders(clientId)
        }).pipe(
            map((res: Response) => {
                if (res) {
                    this.monitoringService.clearRuleExecutionLogs();
                    return res;
                }
            }),
            catchError((error: any) => {
                return throwError(() => new Error(error.message));
            })
        );
    }
}
